import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  ViewChildren,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, map, switchMap } from 'rxjs';
import { FlyoutDataType, FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import {
  DataPlatformVisitRow,
  DataPlatformVisitEvent,
  DataPlatformConsentEvent,
  DataPlatformAudienceDefinition,
  DataPlatformVisitCredit,
  DataPlatformVisitStatusResponse,
} from '../../data-platform/data-platform.model';
import { injectCreditedVisitsAccess } from '../../data-platform/credited-visits-access';
import { TranslateService } from '@ngx-translate/core';
import { DataPlatformService } from '../../data-platform/data-platform.service';
import { hasVisitStatusChanged, isOpenVisit, pollVisitStatus } from '../../data-platform/visit-status.utils';
import {
  groupVisitDimensionsByType,
  getDimensionTypeLabel,
  collectVisitTags,
  collectCouponActivity,
  getEngagementStars,
  getTier,
  getTierIcon,
  loginStatusLabelKey,
  getInsightPresets,
  InsightPreset,
  UrlInterestProfileMap,
  buildDimensionTypeDonut,
  buildDimensionTypeDonutFromComposites,
  translateDimensionTypeValue,
  DimensionTypeDonut,
} from '../../data-platform/data-platform.utils';
import { audienceEvaluateRules } from '../../data-platform/audiences/audience-filter-to-dp-rule.utils';
import { CurrentContextService } from '../../core/current-context.service';
import { Currency } from '../../shared/account/Currency';
import { DateService } from '../../shared/services/date-service';
import { PropertyLabelService } from '../../shared/services/property-label.service';
import { WidgetService } from '../../shared/services/widget.service';
import { UrlInterestProfilesService } from '../../data-platform/url-interest-profiles.service';
import { AudienceService } from '../../audience/audience.service';
import { PurchaseListItem } from '../data-platform-purchases-list/data-platform-purchases-list.component';
import { visitOrganization } from '../../data-platform/visit-organization.utils';

interface EventView extends DataPlatformVisitEvent {
  icon: string;
  typeLabel: string;
  label: string;
  timestampDate: Date;
  dimensionLabels: string[];
  widgetLabel: string | null;
  widgetThumbnail: string | null;
  /** True for goal completions and revenue events, which the "Important" filter keeps. */
  important: boolean;
}

interface WidgetLabel {
  label: string;
  thumbnail: string | null;
}

interface CreditView {
  name: string;
  thumbnail: string | null;
  typeLabelKey: string;
  revenue: number;
}

@Component({
  selector: 'app-data-platform-visit-flyout',
  templateUrl: './data-platform-visit-flyout.component.html',
  styleUrls: ['./data-platform-visit-flyout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataPlatformVisitFlyoutComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() flyoutOpeningData: FlyoutOpeningData;

  @ViewChildren('flyoutHeaderEl', { read: ElementRef })
  private headerEls: QueryList<ElementRef<HTMLElement>>;

  protected visit: DataPlatformVisitRow;

  protected get organization(): string | null {
    return visitOrganization(this.visit);
  }
  protected engagementStarIcons: string[] = [];
  protected tier: 'hot' | 'active' | 'warming' | 'cold' = 'cold';
  protected tierIcon = 'ac_unit';
  protected tierLabelKey = 'data-platform.tier-cold';
  protected matchedInsights: InsightPreset[] = [];
  protected startedAtDate: Date;
  protected readonly endedAtDate = signal<Date | null>(null);
  protected readonly sessionDurationSeconds = signal(0);
  protected loginStatusKey: string | null = null;
  /** Header: the contact's name when known, else the latest identifier, else the UID. */
  protected headerTitle = '';
  /** Header secondary line: the identifier + email not already shown as the title, deduped. */
  protected headerSubtitle = '';
  /** Email driving the header avatar; empty renders the anonymous icon. */
  protected avatarEmail = '';
  protected pageViewCount: number;
  protected goalCount: number;
  protected totalRevenue: number;
  protected allTags: string[];
  private readonly rawTraits = signal<{ key: string; value: string }[]>([]);
  /** Property key to widget input-field label, e.g. first_name to the label the form showed. */
  private readonly propertyLabels = toSignal(inject(PropertyLabelService).getLabelMap(), {
    initialValue: new Map<string, string>(),
  });
  protected readonly traitsEntries = computed(() =>
    this.rawTraits().map((trait) => {
      const label = this.propertyLabels().get(trait.key) ?? trait.key;
      // Widget input labels often carry their own punctuation ("Choose here:", "Vem?")
      return {
        ...trait,
        label: /[:?!]\s*$/.test(label) ? label : `${label}:`,
        // A value with no space cannot wrap, so the row truncates it instead of overflowing the card.
        isIdentifierValue: !/\s/.test(trait.value ?? ''),
      };
    }),
  );
  protected consentEvents: DataPlatformConsentEvent[] = [];
  protected dimensionTypeDonut: DimensionTypeDonut[] = [];
  protected purchaseItems: PurchaseListItem[] = [];
  protected couponShown = false;
  protected couponCopied = false;
  protected currency: Currency | undefined;
  /** Whether the account has holdout groups enabled; controls the holdout row in General. */
  protected hasHoldout = false;
  /** Empty when the visit was tracked the ordinary way, so the icon only appears when there is something to say. */
  protected privacyTooltip = '';
  /** The two states that should not happen get the warning colour; cookie-free is the feature working. */
  protected privacyIsWarning = false;
  /** A derived uid is shared by everyone on the same network and browser that day, so it is nobody's profile. */
  protected hasProfile = true;
  /** The profile title to navigate back to, when this visit was opened from a profile flyout. */
  protected backTitle = '';
  /** Stepping between the profile's visits (newest first), when opened from a profile flyout. */
  protected showVisitNav = false;
  protected hasNewerVisit = false;
  protected hasOlderVisit = false;
  private siblings: DataPlatformVisitRow[] = [];
  private siblingIndex = -1;

  protected readonly eventViews = signal<EventView[]>([]);
  /** Timeline filter; "important" keeps only goal completions and revenue events. */
  protected readonly eventFilter = signal<'all' | 'important'>('all');
  protected readonly importantEventCount = computed(() => this.eventViews().filter((e) => e.important).length);
  protected readonly visibleEvents = computed(() =>
    this.eventFilter() === 'important' ? this.eventViews().filter((e) => e.important) : this.eventViews(),
  );
  protected readonly isLoadingEvents = signal(false);
  /** True while the flyout header is scrolled out of view; shows the condensed sticky header. */
  protected readonly showMiniHeader = signal(false);
  private headerObserver: IntersectionObserver | null = null;

  protected readonly urlInterestProfileMap = signal<UrlInterestProfileMap>(new Map());

  private readonly visitStatus = signal<DataPlatformVisitStatusResponse | null>(null);
  protected readonly isOngoing = computed(() => isOpenVisit(this.visitStatus()));

  private readonly creditedVisitsAccess = injectCreditedVisitsAccess();
  private readonly credits = signal<DataPlatformVisitCredit[]>([]);
  /** Null until the account's widgets have loaded. */
  private readonly widgetLabels = signal<ReadonlyMap<number, WidgetLabel> | null>(null);
  private readonly failedThumbnails = signal<ReadonlySet<string>>(new Set());
  protected readonly creditViews = computed<CreditView[]>(() => {
    if (!this.creditedVisitsAccess()) {
      return [];
    }
    const labels = this.widgetLabels();
    return this.credits().map((credit) => ({
      name: labels?.get(credit.widgetId)?.label ?? String(credit.widgetId),
      thumbnail: this.thumbnail(credit.widgetId, labels),
      typeLabelKey:
        credit.type === 'attribution-incentivized' ? 'data-platform.credit-coupon' : 'data-platform.credit-last-touch',
      revenue: credit.revenue,
    }));
  });

  private readonly flyoutService = inject(FlyoutService);
  private readonly dataPlatformService = inject(DataPlatformService);
  private readonly translateService = inject(TranslateService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly dateService = inject(DateService);
  private readonly widgetService = inject(WidgetService);
  private readonly urlInterestProfilesService = inject(UrlInterestProfilesService);
  private readonly audienceService = inject(AudienceService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly visitChange$ = new Subject<DataPlatformVisitRow>();
  private readonly refreshVisit$ = new Subject<void>();

  constructor() {
    // Load URL interest profile mapping
    this.urlInterestProfilesService
      .getUrlInterestProfileMap()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((map) => {
        this.urlInterestProfileMap.set(map);
        // Re-apply event formatting with updated mapping
        if (this.visit) {
          this.applyEvents(this.visit.events ?? []);
        }
      });

    // Properties for this visit, scoped by session id (surfaces the visit's form/survey answers).
    // switchMap cancels the previous in-flight request when a new visit arrives.
    this.visitChange$
      .pipe(
        takeUntilDestroyed(),
        switchMap((visit) =>
          this.dataPlatformService.getProperties(
            { uid: visit.uid, sessionId: visit.sessionId },
            'visits-flyout-properties',
          ),
        ),
      )
      .subscribe({
        next: (result) => {
          if (result?.isPositive() && result.data) {
            this.rawTraits.set(Object.entries(result.data.properties).map(([key, value]) => ({ key, value })));
          }
        },
      });

    // Consent events
    this.visitChange$
      .pipe(
        takeUntilDestroyed(),
        switchMap((visit) =>
          this.dataPlatformService.getConsentEvents(
            { uid: visit.uid, from: visit.startedAt, to: visit.endedAt },
            'visits-flyout-consent',
          ),
        ),
      )
      .subscribe({
        next: (result) => {
          if (result?.isPositive() && result.data) {
            this.consentEvents = result.data.events;
          }
        },
      });

    // Audience insights — presets + all compatible saved audiences, no duplicates
    this.visitChange$
      .pipe(
        takeUntilDestroyed(),
        switchMap((visit) => {
          const siteIds = this.flyoutOpeningData?.dataPlatformFilter?.siteIds;
          if (!siteIds?.length) return EMPTY;
          const ctx = this.currentContextService.currentContextBS.getValue();
          const purchaseName = ctx?.account?.metricsGoalNames?.[0] || 'purchase';
          const presets = getInsightPresets(purchaseName);
          const presetIds = new Set(presets.map((p) => p.id));

          return this.audienceService.getAllAudiences().pipe(
            switchMap((audiences) => {
              const savedMeta: { id: string; label: string }[] = [];
              const savedDefs: DataPlatformAudienceDefinition[] = [];
              for (const a of audiences) {
                if (!a.id || !a.name || a.isDefault || presetIds.has(a.id)) {
                  continue;
                }
                const rules = audienceEvaluateRules(a);
                if (!rules) {
                  continue;
                }
                savedMeta.push({ id: a.id, label: a.name });
                savedDefs.push({ id: a.id, rules });
              }

              const allDefs: DataPlatformAudienceDefinition[] = [
                ...presets.map((p) => ({ id: p.id, rules: p.rules })),
                ...savedDefs,
              ];

              return this.dataPlatformService
                .evaluateAudiences({ siteIds, uid: visit.uid, audiences: allDefs }, 'visits-flyout-insights')
                .pipe(map((result) => ({ result, presets, savedMeta })));
            }),
          );
        }),
      )
      .subscribe({
        next: ({ result, presets, savedMeta }) => {
          if (result?.isPositive() && result.data) {
            const matchedIds = new Set(result.data.matches.filter((m) => m.matched).map((m) => m.audienceId));
            const matchedPresets = presets.filter((p) => matchedIds.has(p.id));
            const matchedSaved: InsightPreset[] = savedMeta
              .filter((d) => matchedIds.has(d.id))
              .map((d) => ({ id: d.id, label: d.label, icon: 'bookmark', variant: 'neutral' as const, rules: [] }));
            this.matchedInsights = [...matchedPresets, ...matchedSaved];
          }
        },
      });

    // Visit events
    this.visitChange$
      .pipe(
        takeUntilDestroyed(),
        switchMap((visit) => {
          if (visit.events?.length > 0) {
            // Events already loaded (e.g. from profile flyout which requests includeEvents=true)
            this.applyEvents(visit.events);
            return EMPTY;
          }
          // List view: populate summaries immediately from pre-computed fields and
          // lazy-load the full event list for the event timeline and dimension donuts.
          this.pageViewCount = visit.pageViewCount ?? 0;
          this.goalCount = visit.goalCount ?? 0;
          this.totalRevenue = visit.totalRevenue ?? 0;
          this.allTags = visit.tags ?? [];
          // Use topDimensions for dimension donut when events are not loaded
          this.dimensionTypeDonut = buildDimensionTypeDonutFromComposites(
            visit.topDimensions ?? [],
            this.urlInterestProfileMap(),
            this.translateService,
            visit.dimensionScores,
          );
          this.eventViews.set([]);
          if (!this.flyoutOpeningData?.dataPlatformFilter) {
            this.applyEvents([]);
            return EMPTY;
          }
          this.isLoadingEvents.set(true);
          return this.loadVisitWithEvents(visit, 'visits-flyout-events');
        }),
      )
      .subscribe({
        next: (result) => {
          const flyoutVisit = result?.isPositive() ? result.data?.visits?.[0] : null;
          if (flyoutVisit && this.visit) {
            this.applySessionFields(flyoutVisit);
          }
          this.applyEvents(flyoutVisit?.events ?? []);
        },
        error: () => this.applyEvents([]),
      });

    // Visit status
    this.visitChange$
      .pipe(
        switchMap((visit) =>
          pollVisitStatus((request) => this.dataPlatformService.getVisitStatus(request, 'visits-flyout-status'), visit),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => {
        const previous = this.visitStatus();
        this.visitStatus.set(status);
        if (previous && status && hasVisitStatusChanged(previous, status)) {
          this.refreshVisit$.next();
        }
      });

    // Visit refresh
    this.visitChange$
      .pipe(
        switchMap((visit) =>
          this.refreshVisit$.pipe(
            switchMap(() => this.loadVisitWithEvents(visit, 'visits-flyout-refresh').pipe(catchError(() => EMPTY))),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        const flyoutVisit = result?.isPositive() ? result.data?.visits?.[0] : null;
        if (flyoutVisit) {
          this.applySessionFields(flyoutVisit);
          this.applyEvents(flyoutVisit.events ?? []);
        }
      });
  }

  // Led by the short label: a tooltip that opens with six lines of explanation is read by nobody.
  private resolvePrivacy(): void {
    this.hasProfile = !this.visit?.isStateless;
    const normal = !this.visit || (!this.visit.isStateless && !this.visit.isOptOut);
    if (normal) {
      this.privacyTooltip = '';
      this.privacyIsWarning = false;
      return;
    }

    const cookieFree = this.visit.isStateless && this.visit.isOptOut;
    const key = cookieFree
      ? 'tracking-cookie-free'
      : this.visit.isOptOut
        ? 'tracking-denied-stored'
        : 'tracking-allowed-derived';
    const label = this.translateService.instant(`data-platform.${key}`);
    const hint = this.translateService.instant(`data-platform.${key}-hint`);
    this.privacyTooltip = `${label} - ${hint}`;
    this.privacyIsWarning = !cookieFree;
  }

  ngOnChanges() {
    const ctx = this.currentContextService.currentContextBS.getValue();
    this.currency = ctx?.account?.currency as Currency;
    this.hasHoldout = !!ctx?.account?.holdoutGroupSettings;
    this.visit = this.flyoutOpeningData?.dataPlatformVisitRow;
    // After the visit is set: it is what the hint is read from.
    this.resolvePrivacy();
    this.backTitle = this.flyoutOpeningData?.dataPlatformBackTitle ?? '';
    this.siblings = this.flyoutOpeningData?.dataPlatformVisitSiblings ?? [];
    this.siblingIndex = this.visit ? this.siblings.findIndex((row) => row.sessionId === this.visit.sessionId) : -1;
    this.showVisitNav = this.siblingIndex >= 0 && this.siblings.length > 1;
    this.hasNewerVisit = this.siblingIndex > 0;
    this.hasOlderVisit = this.siblingIndex >= 0 && this.siblingIndex < this.siblings.length - 1;
    if (this.visit) {
      this.headerTitle = this.visit.name?.trim() || this.visit.identifier || `UID ${this.visit.uid}`;
      const headerSubs = [this.visit.identifier, this.visit.email].filter(
        (v): v is string => !!v && v !== this.headerTitle,
      );
      this.headerSubtitle = [...new Set(headerSubs)].join(' · ');
      this.avatarEmail = this.visit.email || (this.visit.identifier?.includes('@') ? this.visit.identifier : '');
      this.startedAtDate = this.dateService.utcToLocalTime(this.visit.startedAt);
      this.applyVisitMetadata();
      this.visitStatus.set(null);
      this.rawTraits.set([]);
      this.consentEvents = [];
      this.matchedInsights = [];
      this.couponShown = false;
      this.couponCopied = false;
      this.credits.set([]);
      this.eventFilter.set('all');
      this.visitChange$.next(this.visit);
    }
  }

  ngAfterViewInit() {
    this.observeHeader();
    this.headerEls.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.observeHeader());
  }

  ngOnDestroy() {
    this.headerObserver?.disconnect();
  }

  /** Toggles the condensed sticky header based on whether the full header is scrolled out of view. */
  private observeHeader() {
    this.headerObserver?.disconnect();
    this.headerObserver = null;
    const el = this.headerEls?.first?.nativeElement;
    if (!el) {
      this.showMiniHeader.set(false);
      return;
    }
    this.headerObserver = new IntersectionObserver(([entry]) => this.showMiniHeader.set(!entry.isIntersecting));
    this.headerObserver.observe(el);
  }

  private loadVisitWithEvents(visit: DataPlatformVisitRow, feature: string) {
    const siteIds = this.flyoutOpeningData?.dataPlatformFilter?.siteIds;
    if (!siteIds?.length) {
      return EMPTY;
    }
    return this.dataPlatformService.getVisits(
      {
        // Not the opener's range: Profiles' default 14 days can leave the visit out.
        filter: { siteIds, from: visit.startedAt },
        uid: visit.uid,
        sessionId: visit.sessionId,
        includeEvents: true,
        pageSize: 1,
        includeTotalCount: false,
      },
      feature,
    );
  }

  /** Fields only the full visit response carries. */
  private applySessionFields(flyoutVisit: DataPlatformVisitRow) {
    this.visit.sessionDuration = flyoutVisit.sessionDuration;
    this.visit.endedAt = flyoutVisit.endedAt;
    this.visit.engagementScore = flyoutVisit.engagementScore;
    this.visit.loginStatus = flyoutVisit.loginStatus;
    this.visit.browser = flyoutVisit.browser;
    this.visit.os = flyoutVisit.os;
    this.visit.deviceType = flyoutVisit.deviceType;
    this.visit.geoCountry = flyoutVisit.geoCountry;
    this.visit.netName = flyoutVisit.netName;
    this.visit.isIsp = flyoutVisit.isIsp;
    this.visit.organization = flyoutVisit.organization;
    this.visit.referrerType = flyoutVisit.referrerType;
    this.visit.referrerDomain = flyoutVisit.referrerDomain;
    this.visit.utmCampaignName = flyoutVisit.utmCampaignName;
    this.visit.utmCampaignSource = flyoutVisit.utmCampaignSource;
    this.visit.sessionSource = flyoutVisit.sessionSource;
    this.visit.referrerApp = flyoutVisit.referrerApp;
    this.visit.distributedCoupons = flyoutVisit.distributedCoupons;
    this.visit.credits = flyoutVisit.credits;
    this.applyVisitMetadata();
    // An ongoing visit keeps scoring, so hand the fresh values to the list that opened this.
    this.flyoutService.dataPlatformVisitRefreshedBS.next(this.visit);
  }

  private applyVisitMetadata() {
    this.engagementStarIcons = getEngagementStars(this.visit.engagementScore);
    this.tier = getTier(this.visit.engagementScore);
    this.tierIcon = getTierIcon(this.tier);
    this.tierLabelKey = `data-platform.tier-${this.tier}`;
    this.endedAtDate.set(this.dateService.utcToLocalTime(this.visit.endedAt));
    this.sessionDurationSeconds.set(this.visit.sessionDuration);
    this.loginStatusKey = loginStatusLabelKey(this.visit.loginStatus);
  }

  private applyEvents(events: DataPlatformVisitEvent[]) {
    this.isLoadingEvents.set(false);
    const map = this.urlInterestProfileMap();
    this.pageViewCount = events.filter((e) => e.eventType === 'pageview').length;
    this.goalCount = events.filter((e) => e.goalName).length;
    this.totalRevenue = events.reduce((sum, e) => sum + (e.revenue || 0), 0);
    this.allTags = this.visit.tags?.length ? this.visit.tags : collectVisitTags(events);
    const fromEvents = groupVisitDimensionsByType(events);
    this.dimensionTypeDonut =
      fromEvents.length > 0
        ? fromEvents.map((g) => buildDimensionTypeDonut(g.type, g.affinities, map, this.translateService))
        : buildDimensionTypeDonutFromComposites(
            this.visit.topDimensions ?? [],
            map,
            this.translateService,
            this.visit.dimensionScores,
          );
    this.buildEventViews(events, map);
    this.buildPurchaseItems(events);
    const coupons = collectCouponActivity(events);
    this.couponShown = coupons.shown;
    this.couponCopied = coupons.copied;
    this.credits.set(this.visit.credits ?? []);
    this.loadWidgetLabels(events);
  }

  private buildPurchaseItems(events: DataPlatformVisitEvent[]) {
    this.purchaseItems = events
      .filter((e) => e.eventType === 'purchase')
      .map((e) => ({
        key: e.timestamp,
        date: this.dateService.utcToLocalTime(e.timestamp),
        amount: e.revenue ?? 0,
      }));
  }

  protected closeFlyout() {
    this.flyoutService.close();
  }

  /** Opens the profile flyout for this visit's profile. */
  protected openNewerVisit() {
    this.openSibling(-1);
  }

  protected openOlderVisit() {
    this.openSibling(1);
  }

  /** Opens the neighboring visit in the profile's list (siblings are ordered newest first). */
  private openSibling(offset: number) {
    const target = this.siblings[this.siblingIndex + offset];
    if (!target) {
      return;
    }
    this.flyoutService.open({
      type: FlyoutDataType.DataPlatformVisit,
      uid: target.uid,
      dataPlatformVisitRow: target,
      dataPlatformFilter: this.flyoutOpeningData?.dataPlatformFilter,
      dataPlatformBackTitle: this.flyoutOpeningData?.dataPlatformBackTitle,
      dataPlatformVisitSiblings: this.siblings,
    } as FlyoutOpeningData);
  }

  protected openProfile() {
    if (!this.visit) return;
    this.flyoutService.open({
      type: FlyoutDataType.DataPlatformProfile,
      uid: this.visit.uid,
      dataPlatformFilter: this.flyoutOpeningData?.dataPlatformFilter,
      dataPlatformBackVisitRow: this.visit,
    } as FlyoutOpeningData);
  }

  private buildEventViews(events: DataPlatformVisitEvent[], map: UrlInterestProfileMap) {
    const labels = this.widgetLabels();
    this.eventViews.set(
      events.map((e) => ({
        ...e,
        icon: this.resolveEventIcon(e),
        typeLabel: this.resolveEventTypeLabel(e.eventType),
        label: this.resolveEventLabel(e),
        timestampDate: this.dateService.utcToLocalTime(e.timestamp),
        dimensionLabels: (e.dimensions ?? []).map((d) => {
          const colonIdx = d.indexOf(':');
          if (colonIdx === -1) {
            return getDimensionTypeLabel(d, this.translateService);
          }
          const type = d.substring(0, colonIdx);
          const value = d.substring(colonIdx + 1);
          return translateDimensionTypeValue(type, value, map, this.translateService);
        }),
        widgetLabel: e.widgetId ? (labels?.get(e.widgetId)?.label ?? String(e.widgetId)) : null,
        widgetThumbnail: e.widgetId ? this.thumbnail(e.widgetId, labels) : null,
        important: !!e.goalName || (e.revenue ?? 0) > 0,
      })),
    );
  }

  private loadWidgetLabels(events: DataPlatformVisitEvent[]) {
    const creditIds = this.credits().map((credit) => credit.widgetId);
    const widgetIds = [...new Set([...events.map((e) => e.widgetId), ...creditIds].filter((id): id is number => !!id))];
    if (!widgetIds.length) return;

    this.widgetService
      .getAllWidgetsSimpleInfo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((widgets) => {
        const labels = new Map<number, WidgetLabel>();
        for (const w of widgets ?? []) {
          if (w.id == null) continue;
          const name = w.name ?? '';
          const campaign = w.campaignName ?? '';
          labels.set(w.id, {
            label: campaign && name ? `${campaign} (${name})` : campaign || name,
            thumbnail: w.thumbnail || null,
          });
        }
        this.widgetLabels.set(labels);
        const map = this.urlInterestProfileMap();
        this.buildEventViews(events, map);
      });
  }

  private thumbnail(widgetId: number, labels: ReadonlyMap<number, WidgetLabel> | null): string | null {
    const thumbnail = labels?.get(widgetId)?.thumbnail;
    return thumbnail && !this.failedThumbnails().has(thumbnail) ? thumbnail : null;
  }

  protected onThumbnailError(thumbnail: string) {
    this.failedThumbnails.update((failed) => new Set(failed).add(thumbnail));
    this.eventViews.update((views) =>
      views.map((view) => (view.widgetThumbnail === thumbnail ? { ...view, widgetThumbnail: null } : view)),
    );
  }

  private resolveEventIcon(event: DataPlatformVisitEvent): string {
    if (event.goalName?.startsWith('visitedpage-')) return 'bookmark';
    if (event.goalName) return 'flag';
    switch (event.eventType) {
      case 'pageview':
        return 'web';
      case 'event':
        return 'touch_app';
      case 'session':
        return 'person';
      case 'goal':
        return 'flag';
      case 'ping':
        return 'sensors';
      case 'purchase':
        return 'shopping_cart';
      case 'widget-clickthrough':
      case 'widget-conversion':
      case 'widget-copy-coupon':
      case 'widget-dismissal':
      case 'widget-open':
      case 'widget-survey-response':
        return 'widgets';
      default:
        return 'radio_button_unchecked';
    }
  }

  /** "pageview" reads "Page view"; unknown types keep their raw name, as in the filter bar. */
  private resolveEventTypeLabel(eventType: string): string {
    const key = `data-platform.event-type-${eventType}`;
    const translated = this.translateService.instant(key);
    return translated === key ? eventType : translated;
  }

  private resolveEventLabel(event: DataPlatformVisitEvent): string {
    if (event.goalName)
      return event.goalName.startsWith('visitedpage-') ? event.goalName.slice('visitedpage-'.length) : event.goalName;
    if (event.actionTitle) return event.actionTitle;
    if (event.actionUrl) return event.actionUrl;
    return event.eventType;
  }
}
